// src/components/splash/AnimatedRabexMark.tsx  (UPDATED)
import * as React from 'react';
import { Box } from '@rabex-kit/rabex-ui';
import { keyframes } from '@mui/material/styles';

/**
 * "Market pulse" — inverse-phase drift + breathing scale.
 *
 * Each arm runs the same keyframe; the second arm has a half-period
 * negative delay (-1s of 2s) so when one peaks the other troughs.
 * Reads like a green candle / red candle in real time — appropriate
 * for an exchange's loading and offline states.
 *
 * Transform-only → single GPU composite, no layout/paint per frame.
 */
const marketPulse = keyframes`
    0%, 100% {
        transform: translateY(-6px) scale(1.06);
        opacity: 1;
    }
    50% {
        transform: translateY(6px) scale(0.94);
        opacity: 0.88;
    }
`;

interface Props {
  /** Width in px. Height auto-scales to maintain the 45:60 aspect. */
  size?: number;
  animated?: boolean;
}

export default function AnimatedRabexMark({
  size = 45,
  animated = true
}: Props) {
  const width = size;
  const height = (size * 60) / 45;

  // Encoded as % so inner SVGs scale with the outer frame alone.
  const armW = `${(21 / 45) * 100}%`;
  const armH = `${(44 / 60) * 100}%`;

  const animationRule = (delay: string) =>
    animated ? `${marketPulse} 2s ease-in-out ${delay} infinite` : 'none';

  const armBase = {
    position: 'absolute' as const,
    width: armW,
    height: armH,
    willChange: animated ? 'transform, opacity' : undefined,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
  };

  return (
    <Box sx={{ position: 'relative', width, height, flexShrink: 0 }}>
      {/* LEFT arm — taller, sits a bit higher */}
      <Box
        component="svg"
        // @ts-ignore
        viewBox="0 0 21 44"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        sx={{
          ...armBase,
          left: 0,
          top: `${(1.5 / 60) * 100}%`,
          animation: animationRule('0s')
        }}>
        <path
          d="M0 40.8931C0 42.5879 1.82262 43.6575 3.30221 42.831L9.31088 39.4745C10.013 39.0823 10.4481 38.3409 10.4481 37.5367V17.8017C10.4481 16.9966 10.8841 16.2545 11.5874 15.8627L19.7569 11.3106C20.4602 10.9187 20.8962 10.1767 20.8962 9.37154V2.22197C20.8962 0.527628 19.0744 -0.54201 17.5948 0.283619L1.13808 9.46678C0.435441 9.85887 0 10.6005 0 11.4051V40.8931Z"
          fill="#3861FB"
        />
      </Box>

      {/* RIGHT arm — sits lower, half-period delay → inverse phase */}
      <Box
        component="svg"
        // @ts-ignore
        viewBox="0 0 21 44"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        sx={{
          ...armBase,
          left: `${(24 / 45) * 100}%`,
          top: `${(10 / 60) * 100}%`,
          animation: animationRule('-1s')
        }}>
        <path
          d="M0 33.8501V41.0036C0 42.6974 1.82067 43.767 3.30023 42.9426L19.7594 33.7706C20.4627 33.3787 20.8986 32.6367 20.8986 31.8316V2.22242C20.8986 0.528862 19.0783 -0.54086 17.5988 0.283206L11.5901 3.62985C10.8866 4.02168 10.4505 4.7638 10.4505 5.56907V25.4082C10.4505 26.2126 10.0154 26.954 9.31309 27.3462L1.13741 31.9121C0.435146 32.3043 0 33.0457 0 33.8501Z"
          fill="#3861FB"
        />
      </Box>
    </Box>
  );
}
